import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Trash, FloppyDisk } from '@phosphor-icons/react';

export default function BotSettingsPage() {
  const { selectedBot, bots } = useOutletContext();
  const [settings, setSettings] = useState(null);
  const [botName, setBotName] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    if (!selectedBot) { setSettings(null); return; }
    setLoading(true);
    API.get(`/bots/${selectedBot}/settings`)
      .then(r => { setSettings(r.data.settings); setBotName(r.data.bot_name); })
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, [selectedBot]);

  if (!selectedBot) {
    return (
      <div className="space-y-4" data-testid="bot-settings-page">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Bot Settings</h1>
        <div className="border border-dashed border-border rounded-md p-12 text-center text-muted-foreground">
          Select a bot from the sidebar to configure settings
        </div>
      </div>
    );
  }

  const saveSettings = async () => {
    // Validate & sanitize buttons before saving
    const cleanedButtons = [];
    for (let i = 0; i < (settings.inline_buttons || []).length; i++) {
      const btn = settings.inline_buttons[i];
      const title = (btn.title || '').trim();
      let url = (btn.url || '').trim();

      if (!title && !url) continue; // Skip completely blank row
      if (!title) {
        toast.error(`Button ${i + 1} is missing a title`);
        return;
      }
      if (!url) {
        toast.error(`Button "${title}" is missing a link/URL`);
        return;
      }

      // Auto-correct .c typo e.g. allkhiladi.c -> allkhiladi.com
      if (url.endsWith('.c')) {
        url = url + 'om';
      }

      // Prepend https:// if protocol is missing
      if (!url.startsWith('http://') && !url.startsWith('https://') && !url.startsWith('tg://')) {
        url = 'https://' + url;
      }

      cleanedButtons.push({ ...btn, title, url });
    }

    setSaving(true);
    try {
      if (imageFile) {
        const formData = new FormData();
        formData.append('file', imageFile);
        const res = await API.post(`/bots/${selectedBot}/upload-welcome-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (res?.data?.image_url) {
          setSettings(s => ({ ...s, welcome_image: res.data.image_url }));
        }
      }
      setSettings(s => ({ ...s, inline_buttons: cleanedButtons }));
      await API.put(`/bots/${selectedBot}/settings`, {
        welcome_text_1: settings.welcome_text_1,
        welcome_text_2: settings.welcome_text_2,
        inline_buttons: cleanedButtons,
        request_mobile: settings.request_mobile,
        show_button_label: settings.show_button_label !== false,
        ai_enabled: settings.ai_enabled,
        openai_api_key: settings.openai_api_key,
        ai_prompt: settings.ai_prompt,
        quick_replies: settings.quick_replies,
      });
      toast.success('Settings saved');
      setImageFile(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to save';
      toast.error(typeof msg === 'string' ? msg : 'Failed to save');
    } finally { setSaving(false); }
  };

  const addButton = () => {
    setSettings(s => ({ ...s, inline_buttons: [...(s.inline_buttons || []), { title: '', url: '' }] }));
  };

  const removeButton = (idx) => {
    setSettings(s => ({ ...s, inline_buttons: s.inline_buttons.filter((_, i) => i !== idx) }));
  };

  const updateButton = (idx, field, val) => {
    setSettings(s => {
      const btns = [...s.inline_buttons];
      btns[idx] = { ...btns[idx], [field]: val };
      return { ...s, inline_buttons: btns };
    });
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="bot-settings-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Bot Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Configure welcome message for {botName}</p>
      </div>

      {settings && (
        <div className="space-y-6">
          <div className="border border-border rounded-md p-5 space-y-4">
            <h4 className="font-medium text-base">Welcome Image</h4>
            <p className="text-xs text-muted-foreground">Image sent without caption when user starts the bot (sent as separate message)</p>
            {settings.welcome_image && (
              <img src={settings.welcome_image} alt="Welcome" className="w-40 h-40 object-cover rounded-md border border-border" />
            )}
            <div>
              <Input data-testid="welcome-image-upload" type="file" accept="image/*" onChange={e => setImageFile(e.target.files[0])} />
            </div>
          </div>

          <div className="border border-border rounded-md p-5 space-y-4">
            <h4 className="font-medium text-base">Welcome Messages</h4>
            <p className="text-xs text-muted-foreground">Text 1 and Text 2 are sent as separate messages (not merged)</p>
            <div className="space-y-1.5">
              <Label>Welcome Text 1 (sent as separate message)</Label>
              <Textarea
                data-testid="welcome-text-1"
                value={settings.welcome_text_1 || ''}
                onChange={e => setSettings(s => ({ ...s, welcome_text_1: e.target.value }))}
                placeholder="First welcome message..."
                rows={3}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Welcome Text 2 (sent as separate message with inline buttons)</Label>
              <Textarea
                data-testid="welcome-text-2"
                value={settings.welcome_text_2 || ''}
                onChange={e => setSettings(s => ({ ...s, welcome_text_2: e.target.value }))}
                placeholder="Second welcome message..."
                rows={3}
              />
              <p className="text-xs text-muted-foreground">Default: "Please click below the Buttons :"</p>
            </div>
          </div>

          <div className="border border-border rounded-md p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-base">Inline URL Buttons</h4>
              <Button data-testid="add-inline-btn" variant="outline" size="sm" onClick={addButton} className="gap-1">
                <Plus size={14} /> Add Button
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Buttons appear below Welcome Text 2</p>
            {settings.inline_buttons?.map((btn, i) => (
              <div key={i} className="flex gap-2 items-start">
                <div className="flex-1 space-y-1">
                  <Input data-testid={`btn-title-${i}`} value={btn.title} onChange={e => updateButton(i, 'title', e.target.value)} placeholder="Button Title" />
                </div>
                <div className="flex-1 space-y-1">
                  <Input data-testid={`btn-url-${i}`} value={btn.url} onChange={e => updateButton(i, 'url', e.target.value)} placeholder="https://..." />
                </div>
                <Button data-testid={`remove-btn-${i}`} variant="ghost" size="sm" onClick={() => removeButton(i)} className="text-destructive">
                  <Trash size={16} />
                </Button>
              </div>
            ))}
            {(!settings.inline_buttons || settings.inline_buttons.length === 0) && (
              <p className="text-sm text-muted-foreground text-center py-3">No inline buttons configured</p>
            )}
            <div className="flex items-center justify-between pt-3 border-t border-border">
              <div>
                <Label>Show &ldquo;Please click below the Buttons :&rdquo; label</Label>
                <p className="text-xs text-muted-foreground mt-0.5">When OFF and Welcome Text 2 is empty, only buttons are sent (no label text)</p>
              </div>
              <Switch data-testid="settings-show-btn-label" checked={settings.show_button_label !== false} onCheckedChange={(checked) => setSettings(s => ({ ...s, show_button_label: checked }))} />
            </div>
          </div>

          <div className="border border-border rounded-md p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-base">Request Mobile Number</h4>
                <p className="text-xs text-muted-foreground mt-1">When enabled, a "Share Mobile Number" button will appear in welcome messages and broadcasts</p>
              </div>
              <Switch
                data-testid="request-mobile-toggle"
                checked={settings.request_mobile || false}
                onCheckedChange={(checked) => setSettings(s => ({ ...s, request_mobile: checked }))}
              />
            </div>
          </div>

          {/* OpenAI Chat Bot Section */}
          <div className="border border-border rounded-md p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-base">🤖 AI Auto-Reply (OpenAI)</h4>
                <p className="text-xs text-muted-foreground mt-1">Enable GPT-powered auto-reply for this bot</p>
              </div>
              <Switch
                data-testid="ai-enabled-toggle"
                checked={settings.ai_enabled || false}
                onCheckedChange={(checked) => setSettings(s => ({ ...s, ai_enabled: checked }))}
              />
            </div>
            {settings.ai_enabled && (
              <div className="space-y-3 pt-2">
                <div className="space-y-1.5">
                  <Label>OpenAI API Key</Label>
                  <Input
                    data-testid="openai-api-key"
                    type="password"
                    value={settings.openai_api_key || ''}
                    onChange={e => setSettings(s => ({ ...s, openai_api_key: e.target.value }))}
                    placeholder="sk-..."
                  />
                  <p className="text-xs text-muted-foreground">Get your key from platform.openai.com</p>
                </div>
                <div className="space-y-1.5">
                  <Label>AI System Prompt</Label>
                  <Textarea
                    data-testid="ai-prompt"
                    value={settings.ai_prompt || ''}
                    onChange={e => setSettings(s => ({ ...s, ai_prompt: e.target.value }))}
                    placeholder="You are a helpful customer support assistant for..."
                    rows={4}
                  />
                  <p className="text-xs text-muted-foreground">Customize how the AI responds to users</p>
                </div>
              </div>
            )}
          </div>

          {/* Quick Replies Section */}
          <div className="border border-border rounded-md p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-base">⚡ Quick Replies</h4>
              <Button data-testid="add-quick-reply" variant="outline" size="sm" onClick={() => setSettings(s => ({ ...s, quick_replies: [...(s.quick_replies || []), ''] }))} className="gap-1">
                <Plus size={14} /> Add
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Preset messages for fast replies in Live Chat</p>
            {(settings.quick_replies || []).map((qr, i) => (
              <div key={i} className="flex gap-2">
                <Input
                  data-testid={`quick-reply-${i}`}
                  value={qr}
                  onChange={e => setSettings(s => {
                    const qrs = [...(s.quick_replies || [])];
                    qrs[i] = e.target.value;
                    return { ...s, quick_replies: qrs };
                  })}
                  placeholder="e.g., Thank you for contacting us!"
                />
                <Button variant="ghost" size="sm" onClick={() => setSettings(s => ({ ...s, quick_replies: (s.quick_replies || []).filter((_, j) => j !== i) }))} className="text-destructive shrink-0">
                  <Trash size={14} />
                </Button>
              </div>
            ))}
            {(!settings.quick_replies || settings.quick_replies.length === 0) && (
              <p className="text-sm text-muted-foreground text-center py-2">No quick replies configured</p>
            )}
          </div>

          <Button data-testid="save-settings-btn" onClick={saveSettings} disabled={saving} className="gap-2">
            <FloppyDisk size={18} /> {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      )}
    </div>
  );
}
